/**
 * Email Template Utilities & Personalization Engine
 * Handles dynamic variable substitution for Brevo transactional emails
 */

export const DEFAULT_INTERNSHIP_SUBJECT = 'Internship Offer Letter — Full Stack Development | Innovation Hacks';

export const DEFAULT_INTERNSHIP_BODY = `Dear {{candidate_name}},

Congratulations! 🎉 You have been selected for the Innovation Hacks Full Stack Development Internship.

Intern ID: {{id}}

📎 Download your Official QR-Verified Offer Letter attached to this email.

You can also verify your offer letter online at: {{verifyUrl}}

After downloading:

1. Convert the Offer Letter into an image.
2. Post your Offer Letter on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

🚀 After posting, stay active in the official communication channels to receive your task details and internship updates.

Welcome to the Innovation Hacks Family! ❤️

Build. Innovate. Impact. 🌍

Best Regards,
Karthickkumar S
Founder & CEO
Innovation Hacks`;

export const DEFAULT_CERTIFICATE_SUBJECT = '🏆 Certificate of Completion – {{candidate_name}} – {{id}}';

export const DEFAULT_CERTIFICATE_BODY = `Dear {{candidate_name}},

Congratulations on successfully completing your program at {{org}}!

Intern ID: {{id}}
Issue Date: {{issueDate}}

Please find your official Certificate attached to this email.

You can also verify your certificate online at: {{verifyUrl}}

Post your Certificate on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

Best Regards,
Karthickkumar S
Founder & CEO, Innovation Hacks`;

export const DYNAMIC_VARIABLES = [
  { key: '{{candidate_name}}', label: 'Candidate Name', aliases: ['{{name}}'] },
  { key: '{{name}}', label: 'Candidate Name (Short)' },
  { key: '{{intern_id}}', label: 'Intern ID', aliases: ['{{internship_id}}', '{{id}}'] },
  { key: '{{internship_id}}', label: 'Internship ID', aliases: ['{{id}}', '{{intern_id}}'] },
  { key: '{{id}}', label: 'Credential / ID', aliases: ['{{intern_id}}'] },
  { key: '{{email}}', label: 'Recipient Email' },
  { key: '{{college}}', label: 'College / University' },
  { key: '{{department}}', label: 'Department / Branch' },
  { key: '{{start_date}}', label: 'Start Date (YYYY-MM-DD)', aliases: ['{{startDate}}'] },
  { key: '{{end_date}}', label: 'End Date (YYYY-MM-DD)', aliases: ['{{endDate}}'] },
  { key: '{{duration}}', label: 'Internship Duration (e.g. 3 Months)' },
  { key: '{{role}}', label: 'Role / Track' },
  { key: '{{org}}', label: 'Organization Name' },
  { key: '{{authorizedPerson}}', label: 'Authorized Signatory' },
  { key: '{{verifyUrl}}', label: 'Online Verification Link' },
  { key: '{{mentor}}', label: 'Assigned Mentor' },
  { key: '{{issueDate}}', label: 'Document Issue Date' },
  { key: '{{expiryDate}}', label: 'Validity Expiry Date' },
  { key: '{{certType}}', label: 'Certificate Type' },
];

/**
 * Replace all dynamic tags and aliases in a template string with actual candidate/org values
 */
export function resolveTemplate(template = '', data = {}) {
  if (!template || typeof template !== 'string') return '';

  let result = template;

  // Primary replacements
  for (const [key, value] of Object.entries(data)) {
    const val = value !== undefined && value !== null ? String(value) : '';
    // Replace exact tag
    result = result.replace(new RegExp(`{{${key}}}`, 'gi'), val);
  }

  // Handle aliases & snake_case / camelCase bridges
  const aliasMap = {
    'candidate_name': data.name || data.candidate_name || '',
    'name': data.name || data.candidate_name || '',
    'intern_id': data.intern_id || data.internship_id || data.id || '',
    'internship_id': data.intern_id || data.internship_id || data.id || '',
    'id': data.intern_id || data.id || data.internship_id || '',
    'start_date': data.start_date || data.startDate || '',
    'startDate': data.start_date || data.startDate || '',
    'end_date': data.end_date || data.endDate || '',
    'endDate': data.end_date || data.endDate || '',
    'verify_url': data.verifyUrl || data.verify_url || '',
    'verifyUrl': data.verifyUrl || data.verify_url || '',
  };

  for (const [tag, val] of Object.entries(aliasMap)) {
    result = result.replace(new RegExp(`{{${tag}}}`, 'gi'), String(val));
  }

  return result;
}

/**
 * Prepare a comprehensive data dictionary for a candidate
 */
export function buildEmailData(candidate, settings = {}, mode = 'internship') {
  const baseUrl = typeof window !== 'undefined' ? (window.location.origin + window.location.pathname) : 'https://certiflow.app/';
  const credId = candidate.generatedId || candidate.verificationId || candidate.id || 'IH-FS-2026-0001';
  const orgName = settings.organization || (mode === 'internship' ? 'Innovation Hacks' : 'Innovation Hacks Academy');
  const authorized = settings.authorizedPerson || 'HR Team';

  const defaultDuration = candidate.duration || candidate.raw?.Duration || candidate.raw?.duration || '1 Month';
  const defaultStartDate = candidate.startDate || candidate.raw?.StartDate || candidate.raw?.['Start Date'] || '2026-08-01';
  const defaultEndDate = candidate.endDate || candidate.raw?.EndDate || candidate.raw?.['End Date'] || '2026-08-31';

  return {
    name: candidate.name || 'Candidate',
    candidate_name: candidate.name || 'Candidate',
    email: candidate.email || '',
    id: credId,
    intern_id: credId,
    internship_id: credId,
    org: orgName,
    authorizedPerson: authorized,
    verifyUrl: `${baseUrl}#/verify/${credId}`,
    department: candidate.department || candidate.raw?.Department || candidate.raw?.department || 'Technology',
    role: candidate.role || candidate.raw?.Role || candidate.raw?.role || 'Intern',
    startDate: defaultStartDate,
    start_date: defaultStartDate,
    endDate: defaultEndDate,
    end_date: defaultEndDate,
    duration: defaultDuration,
    mentor: candidate.mentor || 'Assigned Lead',
    issueDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    expiryDate: candidate.expiryDate ? new Date(candidate.expiryDate).toLocaleDateString() : 'N/A',
    certType: candidate.certType || (mode === 'internship' ? 'Internship Offer' : 'Certificate of Achievement'),
    college: candidate.college || candidate.raw?.College || candidate.raw?.college || candidate.raw?.Institution || 'University',
  };
}
